import { useState, useEffect } from 'react';

/**
 * Custom React hook to track network connectivity and slow connections.
 * 
 * @param {boolean} loading - Loading state of the component
 * @param {number} [slowTimeout] - Time in ms before showing slow network message (default: 4000ms)
 * @returns {object} - { isOffline, showSlowMsg, message }
 */
export const useNetworkStatus = (loading, slowTimeout = 4000) => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showSlowMsg, setShowSlowMsg] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (isOffline) {
      setShowSlowMsg(true);
      return;
    }

    if (loading) {
      const timer = setTimeout(() => {
        setShowSlowMsg(true);
      }, slowTimeout);
      return () => clearTimeout(timer);
    } else {
      setShowSlowMsg(false);
    }
  }, [loading, isOffline, slowTimeout]);

  return {
    isOffline,
    showSlowMsg,
    message: isOffline
      ? 'No internet connection. Please check your network status.'
      : 'Your connection seems slow. Please check your internet connection.'
  };
};
